import { useState } from "react";
import { toast } from "sonner";
import { Sunrise, Send } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const HOURS = [5, 6, 7, 8, 9, 10, 11, 12];
const label = (h) => (h === 12 ? "12:00 PM" : `${h}:00 AM`);

export const AgendaSettings = () => {
  const { user, setUser } = useAuth();
  const [hour, setHour] = useState(String(user?.agenda_hour || 7));
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/team/${user.user_id}`, { agenda_hour: Number(hour) });
      setUser({ ...user, agenda_hour: Number(hour) });
      toast.success(`Morning agenda will arrive at ${label(Number(hour))} Mountain time`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      await api.post("/me/send-agenda-now");
      toast.success("Today's agenda is on its way to your inbox");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <Card data-testid="agenda-settings-card">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2"><Sunrise className="w-5 h-5 text-primary" /> Morning Agenda Email</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col sm:flex-row sm:items-end gap-4">
        <div className="space-y-1.5">
          <p className="text-sm text-muted-foreground">Choose when your daily summary of appointments and check-ins arrives (Mountain time).</p>
          <Select value={hour} onValueChange={setHour}>
            <SelectTrigger className="w-40" data-testid="agenda-hour-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {HOURS.map((h) => <SelectItem key={h} value={String(h)} data-testid={`agenda-hour-${h}`}>{label(h)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button onClick={save} disabled={saving} data-testid="agenda-save-button">{saving ? "Saving…" : "Save Time"}</Button>
          <Button variant="outline" onClick={sendNow} disabled={sending} data-testid="agenda-send-now-button"><Send className="w-4 h-4 mr-1" /> {sending ? "Sending…" : "Send Now"}</Button>
        </div>
      </CardContent>
    </Card>
  );
};
